export function fetchUpgradeOptions(params) {
  // Support both old format (single serviceid string) and new format (object with serviceid and targetid)
  let serviceid, targetid;
  if (typeof params === 'object') {
    serviceid = params.serviceid;
    targetid = params.targetid;
  } else {
    serviceid = params;
    targetid = null;
  }
  
  // Build URL with optional targetid parameter
  let url = process.env.REACT_APP_BASE_API + 'order/apis/getListUpgrades.php?serviceid=' + serviceid;
  if (targetid) {
    url += '&targetid=' + targetid;
  }
  
  return fetch(url).then((response) => {
    const data = response.json();
    return data.then((result) => {
      // Check if response contains pending upgrade error (updated structure)
      if (result.status === 'error' && result.pending_upgrade) {
        const error = new Error(result.message || 'Pending upgrade detected');
        Object.assign(error, {
          ...result,
          redirect_to_invoice: result.redirect_url ? true : false,
          redirect_url: result.redirect_url,
          invoice_info: result.upgrade_info?.invoice_info
        });
        throw error;
      }
      return result;
    });
  })
}

export function fetchClientServices() {
  return fetch(
    process.env.REACT_APP_BASE_API + 'order/apis/getListUpgrades.php'
  ).then((response) => response.json())
}

export function fetchProductGroups() {
  return fetch(
    process.env.REACT_APP_BASE_API + 'order/apis/getProductName.php'
  ).then((response) => response.json())
}

// Helper function to calculate days in billing cycle
export function getDaysInBillingCycle(billingcycle) {
  const normalizedCycle = String(billingcycle).toLowerCase().trim();
  
  const cycleMap = {
    'monthly': 30,
    'quarterly': 90,
    'semiannually': 180,
    'semi-annually': 180, // Add support for semi-annually variation
    'annually': 365,
    'biennially': 730,
    'triennially': 1095
  };
  return cycleMap[normalizedCycle] || 365;
}

// Helper function to calculate remaining days until next due date
export function calculateRemainingDays(nextDueDate) {
  const today = new Date();
  const dueDate = new Date(nextDueDate);
  const diffTime = dueDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

// Helper function to calculate upgrade price according to WHMCS method
export function calculateUpgradePrice(currentProduct, newProduct, currentBillingCycle, newBillingCycle, remainingDays) {
  if (!currentProduct || !newProduct) {
    return 0;
  }
  
  const currentPricing = currentProduct.pricing?.IDR;
  const newPricing = newProduct.pricing?.IDR;
  
  if (!currentPricing || !newPricing) {
    return 0;
  }

  // Normalize billing cycles for consistent lookup
  const normalizedCurrentCycle = String(currentBillingCycle).toLowerCase().trim();
  const normalizedNewCycle = String(newBillingCycle).toLowerCase().trim();
  
  // Map variations to standard keys
  const cycleMap = {
    'monthly': 'monthly',
    'quarterly': 'quarterly',
    'semiannually': 'semiannually',
    'semi-annually': 'semiannually',
    'annually': 'annually',
    'biennially': 'biennially',
    'triennially': 'triennially'
  };
  
  const currentCycleKey = cycleMap[normalizedCurrentCycle] || normalizedCurrentCycle;
  const newCycleKey = cycleMap[normalizedNewCycle] || normalizedNewCycle;

  const currentPrice = parseFloat(currentPricing[currentCycleKey] || 0);
  const newPrice = parseFloat(newPricing[newCycleKey] || 0);
  
  if (currentPrice <= 0 || newPrice <= 0) {
    return 0;
  }

  const currentDays = getDaysInBillingCycle(currentBillingCycle);
  const newDays = getDaysInBillingCycle(newBillingCycle);
  
  // Check if this is upgrading to a longer billing cycle
  // This applies when upgrading to a longer billing cycle (e.g., 6 months -> 1 year, 1 year -> 2 years)
  const isUpgradingToLongerTerm = newDays > currentDays;
  
  // Use new calculation method for upgrading to longer billing cycle
  if (isUpgradingToLongerTerm) {
    // For billing cycle upgrade: prorate remaining days + full payment for the difference period
    // Example: 
    // - From 6 months to 1 year: prorate sisa 6 bulan + full payment 6 bulan
    // - From 1 year to 2 years: prorate sisa 1 tahun + full payment 1 tahun
    
    // 1. Prorate remaining days with new billing cycle price
    const newPricePerDay = newPrice / newDays;
    const prorateAmount = newPricePerDay * remainingDays;
    
    // 2. Calculate the difference in billing cycles (in days)
    const cycleDifferenceDays = newDays - currentDays;
    
    // 3. Full payment for the difference period using new billing cycle price
    // This is the additional period user needs to pay for
    const fullPaymentAmount = newPricePerDay * cycleDifferenceDays;
    
    // Total = prorate sisa masa aktif + full payment untuk selisih termin
    const totalPayable = prorateAmount + fullPaymentAmount;
    
    return Math.max(0, totalPayable);
  } else {
    // For product upgrade (different product): Use WHMCS standard method
    // Credit for remaining days with old price, debit for remaining days with new price
    const currentPricePerDay = currentPrice / currentDays;
    const amountCredited = currentPricePerDay * remainingDays;
    
    // Use new billing cycle days for new price calculation
    const newPricePerDay = newPrice / newDays;
    const amountDebited = newPricePerDay * remainingDays;
    
    // Total payable = difference
    const totalPayable = amountDebited - amountCredited;
    
    return Math.max(0, totalPayable);
  }
}

// Helper function to get detailed calculation breakdown
export function getUpgradeCalculationBreakdown(currentProduct, newProduct, currentBillingCycle, newBillingCycle, remainingDays) {
  if (!currentProduct || !newProduct) {
    return null;
  }
  
  const currentPricing = currentProduct.pricing?.IDR;
  const newPricing = newProduct.pricing?.IDR;
  
  if (!currentPricing || !newPricing) {
    return null;
  }

  // Normalize billing cycles for consistent lookup
  const normalizedCurrentCycle = String(currentBillingCycle).toLowerCase().trim();
  const normalizedNewCycle = String(newBillingCycle).toLowerCase().trim();
  
  // Map variations to standard keys
  const cycleMap = {
    'monthly': 'monthly',
    'quarterly': 'quarterly',
    'semiannually': 'semiannually',
    'semi-annually': 'semiannually',
    'annually': 'annually',
    'biennially': 'biennially',
    'triennially': 'triennially'
  };
  
  const currentCycleKey = cycleMap[normalizedCurrentCycle] || normalizedCurrentCycle;
  const newCycleKey = cycleMap[normalizedNewCycle] || normalizedNewCycle;

  const currentPrice = parseFloat(currentPricing[currentCycleKey] || 0);
  const newPrice = parseFloat(newPricing[newCycleKey] || 0);
  
  if (currentPrice <= 0 || newPrice <= 0) {
    return null;
  }

  const currentDays = getDaysInBillingCycle(currentBillingCycle);
  const newDays = getDaysInBillingCycle(newBillingCycle);
  
  // Check if this is upgrading to a longer billing cycle
  const isUpgradingToLongerTerm = newDays > currentDays;
  
  // Use new calculation method for upgrading to longer billing cycle
  if (isUpgradingToLongerTerm) {
    // For billing cycle upgrade: prorate + full payment for difference period
    const newPricePerDay = newPrice / newDays;
    const prorateAmount = newPricePerDay * remainingDays;
    
    // Calculate the difference in billing cycles (in days)
    const cycleDifferenceDays = newDays - currentDays;
    
    // Full payment for the difference period using new billing cycle price
    const fullPaymentAmount = newPricePerDay * cycleDifferenceDays;
    
    const totalPayable = prorateAmount + fullPaymentAmount;
    
    return {
      currentPrice,
      newPrice,
      currentDays,
      newDays,
      currentPricePerDay: currentPrice / currentDays,
      newPricePerDay,
      remainingDays,
      isBillingCycleUpgrade: true,
      isUpgradingToLongerTerm: true,
      prorateAmount,
      fullPaymentAmount,
      cycleDifferenceDays: newDays - currentDays, // Add this for display
      totalPayable: Math.max(0, totalPayable)
    };
  } else {
    // For product upgrade: standard WHMCS method
    const currentPricePerDay = currentPrice / currentDays;
    const newPricePerDay = newPrice / newDays;
    const amountCredited = currentPricePerDay * remainingDays;
    const amountDebited = newPricePerDay * remainingDays;
    const totalPayable = amountDebited - amountCredited;
    
    return {
      currentPrice,
      newPrice,
      currentDays,
      newDays,
      currentPricePerDay,
      newPricePerDay,
      remainingDays,
      isBillingCycleUpgrade: false,
      amountCredited,
      amountDebited,
      totalPayable: Math.max(0, totalPayable)
    };
  }
}
